import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@cencosud-cencommerce/paris-uikit';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import { URL_ROUTES } from '@/constants';
import { usePayment } from '@/modules/perseo-billing/hooks';
import type { IOrderIntentResponse } from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import type { ISinglePayment } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { PaymentProcessor } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import {
  setPaymentAvailable,
  setSelectedPaymentMethods
} from '@/modules/perseo-billing/store/slices/billing';
import type { IErrorResponse } from '@/modules/perseo-billing/utils/error-handler';
import { PinpadUrl, useKiosco } from '@/sections/shared/hooks/useKiosco';

import PaymentMethodTitle from './components/title/payment-method-title';

interface KioscoPaymentMethodsElement extends HTMLElement {
  onOrderComplete: ({
    orderNumber,
    orderId
  }: {
    orderNumber: string;
    orderId: string;
  }) => void;
  commitOrderStatusResponse: unknown;
  amount?: number;
  amountCencosud?: number;
  version?: string;
  createOrderIntent: () => void;
  orderIntent: IOrderIntentResponse | null;
  orderIntentError: IErrorResponse | null;
  commitOrderStatus: (value: Record<string, string | number>) => void;
}

interface AngularElementWrapperProps {
  orderTotal: number;
  cencosudPrice: number;
}

const paymentMethodToAdd: ISinglePayment = {
  paymentProcessor: PaymentProcessor.PINPAD
};

const KioscoPaymentMethods: React.FC<AngularElementWrapperProps> = ({
  orderTotal,
  cencosudPrice
}) => {
  const dispatch = useAppDispatch();
  const {
    kioscoOrderIntent: orderIntent,
    kioscoOrderIntentError: orderIntentError
  } = useAppSelector((state) => state.billing);

  const billing = useAppSelector((state) => state.billing);
  const navigate = useNavigate();

  const { selectedPaymentMethods, isPaymentAvailable } = billing || {};

  useEffect(() => {
    if (!selectedPaymentMethods || !isPaymentAvailable) {
      dispatch(
        setSelectedPaymentMethods({
          payments: [paymentMethodToAdd]
        })
      );
      dispatch(setPaymentAvailable(true));
    }
  }, [dispatch, selectedPaymentMethods, isPaymentAvailable]);

  const { config, sendMessageToKiosco, commitOrderStatus } = useKiosco();
  const { handleSubmitForm } = usePayment();
  const angularElementRef = useRef<KioscoPaymentMethodsElement | null>(null);

  const [loaded, setLoaded] = useState({ script: false, cart: false });

  const createOrderIntent = useCallback(handleSubmitForm, [handleSubmitForm]);

  const confirmOrderFunction = useCallback(
    (value: Record<string, string | number>): void => {
      sendMessageToKiosco({
        type: 'commitOrder',
        value
      });
    },
    [sendMessageToKiosco]
  );

  const onOrderComplete = useCallback(
    ({ orderId }: { orderId: string }) => {
      navigate(
        `${URL_ROUTES.BILLING_ORDER_PROCESSING_SUCCESS_PATH}?orderId=${orderId}&device=kiosco`
      );
    },
    [navigate]
  );

  const currentVersion = useMemo(() => {
    if (config) {
      return config.version;
    }
    return '';
  }, [config]);

  const sourceJs = useMemo(() => {
    if (config) {
      return config.pinpadKey;
    }
    return '';
  }, [config]);

  useEffect(() => {
    if (window.customElements.get('kiosco-payment-methods')) {
      return;
    }
    const script = document.createElement('script');
    script.onload = () => {
      setLoaded({ ...loaded, script: true });
    };

    const isValidKey = (key: string): key is keyof typeof PinpadUrl => {
      return key in PinpadUrl;
    };

    if (isValidKey(sourceJs)) {
      script.src = PinpadUrl[sourceJs];
    }

    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [sourceJs, loaded]);

  useEffect(() => {
    if ((cencosudPrice ?? 0) != 0 && !loaded.cart) {
      setLoaded({ ...loaded, cart: true });
    }
    if (angularElementRef.current) {
      angularElementRef.current.amount = orderTotal;
      angularElementRef.current.amountCencosud =
        cencosudPrice && cencosudPrice > 0 && cencosudPrice < orderTotal
          ? cencosudPrice
          : orderTotal;
      angularElementRef.current.version = currentVersion;
      angularElementRef.current.orderIntent = orderIntent;
      angularElementRef.current.orderIntentError = orderIntentError;
      angularElementRef.current.commitOrderStatusResponse = commitOrderStatus;

      angularElementRef.current.createOrderIntent = createOrderIntent;
      angularElementRef.current.commitOrderStatus = confirmOrderFunction;
      angularElementRef.current.onOrderComplete = onOrderComplete;
    }
  }, [
    orderTotal,
    cencosudPrice,
    loaded,
    currentVersion,
    orderIntent,
    orderIntentError,
    commitOrderStatus,
    createOrderIntent,
    confirmOrderFunction,
    onOrderComplete
  ]);

  const isLoading = useMemo(() => {
    return !loaded.script || !loaded.cart || !currentVersion;
  }, [loaded, currentVersion]);

  return (
    <div className="co-flex co-flex-col co-gap-24px">
      <PaymentMethodTitle title={'Medios de pagos físicos'} />
      <div>
        {isLoading && <Spinner></Spinner>}
        {!isLoading &&
          React.createElement('kiosco-payment-methods', {
            ref: angularElementRef,
            version: currentVersion,
            amount: orderTotal,
            orderIntent,
            orderIntentError,
            commitOrderStatusResponse: commitOrderStatus,
            amountCencosud: cencosudPrice,
            createOrderIntent: createOrderIntent,
            commitOrderStatus: confirmOrderFunction,
            onOrderComplete: onOrderComplete
          })}
      </div>
    </div>
  );
};

export default KioscoPaymentMethods;
